import { differenceInCalendarDays } from 'date-fns';
import styled from 'styled-components';
import { Icon } from '../../../../../components/common/icon/Icon';
import { useCheckUserLoggedIn } from '../../../../../hooks/user/useCheckUserLoggedIn';
import { IProduct } from '../../../../../interfaces/Product.interfaces';
import {
  calculateTotalAddPrice,
  calculateTotalSubtractPrice,
} from '../../../../../utils/calculator/calculatePrice.util';
import { formatPrice } from '../../../../../utils/formatter/formatPrice.util';
import { PriceScreenSection } from '../../../components/PriceScreenSection';
import { SecitionDivideLine } from './SecitionDivideLine';

interface ProductInfoAndPriceSectionProps {
  product: IProduct;
  category: 'PLUVA' | 'PARTNER';
}

export const ProductInfoAndPriceSection = ({ product, category }: ProductInfoAndPriceSectionProps) => {
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  return (
    <Container>
      <MetaDataAndTitleContainer>
        <MetaDataContainer>
          <MetaData>{`${product.location}`}</MetaData>
          <MetaData>{'·'}</MetaData>
          <MetaData>{`${differenceInCalendarDays(new Date(), product.createdAt).toLocaleString()}일 전`}</MetaData>
        </MetaDataContainer>
        <Title>{product.title}</Title>
      </MetaDataAndTitleContainer>

      <PriceInfoContainer>
        {!isUserLoggedIn && <PriceScreenSection product={product} pageType={'pdp'} />}

        <PriceInfo>
          <PriceInfoLeftSection>
            <ProductPrice>{`${formatPrice(product.price)}원`}</ProductPrice>
            <OriginPrice>{`${formatPrice(calculateTotalAddPrice(product.priceDetails))}원`}</OriginPrice>
          </PriceInfoLeftSection>
          <PriceInfoRightSection>
            <Discount>{`${formatPrice(calculateTotalSubtractPrice(product.priceDetails))}원 할인`}</Discount>
          </PriceInfoRightSection>
        </PriceInfo>

        <InsallmentSection>
          <InstallmentContainer>
            <Installment>
              <InstallmentLeftSection>
                <Icon name={'Calendar24'} width={20} height={20} />
                <InstallmentTitle>{'24개월 할부시'}</InstallmentTitle>
              </InstallmentLeftSection>
              <InstallmentRightSection>
                <InstallmentPrice>{`월 ${formatPrice(
                  (product.installmentOf24MonthPrice - product.price * 0.3) / 24,
                )}원`}</InstallmentPrice>
              </InstallmentRightSection>
            </Installment>

            <SecitionDivideLine />

            <Installment>
              <InstallmentLeftSection>
                <Icon name={'Calendar12'} width={20} height={20} />
                <InstallmentTitle>{'12개월 할부시'}</InstallmentTitle>
              </InstallmentLeftSection>
              <InstallmentRightSection>
                <InstallmentPrice>{`월 ${formatPrice(
                  (product.installmentOf12MonthPrice - product.price * 0.3) / 12,
                )}원`}</InstallmentPrice>
              </InstallmentRightSection>
            </Installment>
          </InstallmentContainer>

          <InstallmentDescriptionContainer>
            <Icon name={'ExclamationMarkCircle'} width={16} height={16} />

            <InstallmentDescription>
              {'30% 선금 기준이며 선금 비중은 상담을 통해 협의가 가능합니다.'}
            </InstallmentDescription>
          </InstallmentDescriptionContainer>
        </InsallmentSection>
      </PriceInfoContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding: 32px var(--radius-large, 16px);
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-large, 16px);

  align-self: stretch;

  position: relative;
`;

const MetaDataAndTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`;

const MetaDataContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
  align-self: stretch;
`;

const MetaData = styled.h2`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* System/Medium/m-light */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 85%; /* 11.9px */
`;
const Title = styled.h1`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const PriceInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;

  position: relative;
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
`;

const ProductPrice = styled.span`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);

  /* System/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 15.3px */
`;

const OriginPrice = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
  text-decoration-line: line-through;
`;

const Discount = styled.span`
  display: flex;
  padding: 6px 8px;
  align-items: center;

  border-radius: 4px;
  background: #ffdbe0;

  color: #ff3049;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const PriceInfoLeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
  flex: 1 0 0;
`;

const PriceInfoRightSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const InsallmentSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  align-self: stretch;
`;

const InstallmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  padding: var(--radius-large, 16px);
  gap: var(--radius-large, 16px);

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const Installment = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const InstallmentLeftSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 4px;
`;

const InstallmentRightSection = styled.div``;

const InstallmentPrice = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 11.9px */
`;

const InstallmentDescriptionContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--radius-small, 4px);
  align-self: stretch;
`;

const InstallmentDescription = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Body/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 18px */
`;

const InstallmentTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
